import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { Observable } from 'rxjs/internal/Observable';
import { distinctUntilChanged, map, takeUntil, withLatestFrom } from 'rxjs/operators';

import { FormGroup, UntypedFormControl } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { combineLatest } from 'rxjs';
import {
    BaseNestedFieldDefinition,
    FieldType
} from '../../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../../core/profiles/entities/extended-profile-field.entity';
import { PartialRecord } from '../../../../../core/records/entities/partial-record.class';
import { FormValue, FormValueState } from '../../../../../form-controls/entities/form-value.entity';
import { FormGroupRequiredValidator } from '../../../../../form-controls/helpers/form-value-required';
import { throwIfNullOrUndefined } from '../../../../../helpers/typed-reactive-filters.helper';
import { FieldReadHelper } from '../../../../../profile-form/helpers/fields/field-read.helper';
import { ComplexMapFieldValue } from '../../../../entities/field-values/field-type-values.entity';
import {
    ComplexFormValueHelper,
    FormGroupValue
} from '../../../../helpers/complex-form-value/complex-form-value.helper';
import { ChildFieldToProfileField } from '../../../../helpers/to-profile-field/to-profile-field.helper';
import { ShBaseGenericInputFieldComponent } from '../../base-generic-input-field/base-generic-input-field.component';

@Component({
    standalone: false,
    selector: 'sh-complex-map-field',
    templateUrl: './complex-map-input-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShComplexMapInputFieldComponent extends ShBaseGenericInputFieldComponent implements OnInit {
    public childDefinitions$!: Observable<BaseNestedFieldDefinition[]>;
    public childProfileFields$!: Observable<ExtendedBaseProfileField[]>;
    public canEdit$: Observable<boolean>;
    public isBulkEdit$: Observable<boolean>;

    public formGroup: FormGroup = new FormGroup({});

    protected type: FieldType = FieldType.ComplexMapField;

    constructor(protected fieldHelper: FieldReadHelper) {
        super();
        this.initChildDefinitionStreams();
        this.initChildProfileFieldsStream();
        this.initGenericFieldStreams();
    }

    protected init(
        profileField: ExtendedBaseProfileField<BaseNestedFieldDefinition>,
        records: PartialRecord[],
        control: UntypedFormControl
    ): void {
        super.init(profileField, records, control);

        this.initFormValueChangeListener(profileField, control);
    }

    protected initFormValueChangeListener(profileField: ExtendedBaseProfileField, control: UntypedFormControl): void {
        let initialValue: FormGroupValue;

        if (!this.isBatchEdit && (profileField.Required || profileField.FieldDefinition.Required)) {
            control.setValidators(FormGroupRequiredValidator(this.formGroup));
        }

        // Update control with form group value
        this.formGroup.valueChanges
            .pipe(
                map(() => this.formGroup.getRawValue()),
                distinctUntilChanged(isEqual),
                withLatestFrom(this.nestedIndex$),
                takeUntil(this.destroy$)
            )
            .subscribe(([formGroupValue, nestedIndex]) => {
                if (nestedIndex === 0) {
                    if (!initialValue) {
                        initialValue = formGroupValue;
                    }

                    if (isEqual(formGroupValue, initialValue)) {
                        // Do not emit changes if the value has not changed
                        // Used so unchanged values are not overwritten when batch-editing
                        control.setValue([]);
                        return;
                    }

                    // Check if any of the values are indeterminate
                    if (this.formHasIndeterminateValues(formGroupValue)) {
                        // Do not emit changes as long as any of the values are indeterminate
                        control.setValue([]);

                        return;
                    }
                    // Convert to a SET and UNSET form value
                    const recordValues: FormValue<ComplexMapFieldValue>[] = ComplexFormValueHelper.toMetadataValues(
                        formGroupValue,
                        profileField.FieldDefinition
                    );

                    control.setValue(recordValues);
                } else {
                    control.setValue(formGroupValue);
                }
            });
    }

    protected initChildDefinitionStreams(): void {
        this.childDefinitions$ = this.profileField$.pipe(
            map(profileField => profileField.FieldDefinition.Children),
            throwIfNullOrUndefined(`Expected ${this.type} to have children`),
            map(children => {
                if (!Array.isArray(children)) {
                    throw new Error(`Expected ${this.type} children to be an array but was ${children}`);
                }
                if (children.length === 0) {
                    throw new Error(`Expected ${this.type} to have at least 1 child, but has ${children.length}`);
                }
                return children;
            })
        );
    }

    protected initChildProfileFieldsStream(): void {
        // Convert child definitions to profile fields and treat like regular fields
        this.childProfileFields$ = combineLatest([this.childDefinitions$, this.profileField$]).pipe(
            map(([children, profileField]) =>
                children.map(child => ChildFieldToProfileField(child, profileField, this.isBatchEdit))
            )
        );
    }

    private initGenericFieldStreams(): void {
        this.canEdit$ = this.control$.pipe(map(control => control.enabled));

        this.isBulkEdit$ = this.records$.pipe(map(records => records.length > 1 || this.isBatchEdit));
    }

    private formHasIndeterminateValues(formGroupValue: any): boolean {
        return Object.keys(formGroupValue).some(key => {
            // Do not parse list objects. Since the indeterminate values of these will be retained even when setting/unsetting values
            if (Array.isArray(formGroupValue[key]) || formGroupValue[key] === null) {
                return false;
            }

            return formGroupValue[key].state === FormValueState.INDETERMINATE;
        });
    }
}
